/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logInputPivotClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { DemoPageAgreement } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoPageAgreement';
import { DocumentInputStep } from 'src/languageStudio/appShell/prebuiltDemos/components/DocumentInputStep';
import { NextStepsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/NextStepsStep';
import { ResultsStep } from 'src/languageStudio/appShell/prebuiltDemos/components/ResultsStep';
import {
    GeneratedQAsResult,
    MappedQuestions
} from 'src/languageStudio/appShell/prebuiltDemos/generateQuestionsDemo/generateQuestionsSwagger';
import { VisualizedGenerateQuestionsResult } from 'src/languageStudio/appShell/prebuiltDemos/generateQuestionsDemo/VisualizedGenerateQuestionsResult';
import { DemoSample, generateQuestionsDemoSamples } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import {
    generateQuestionsDocLink,
    generateQuestionsSamplesLink,
    textAnalyticsPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import { useQuestionsAnswersGenerator } from 'src/languageStudio/core/hooks/useQuestionsAnswersGenerator';

const messages = defineMessages({
    pageHeader: { id: 'languageStudio.generateQuestionsDemoPage.pageHeader', defaultMessage: 'Generate questions' },
    preview: { id: 'languageStudio.generateQuestionsDemoPage.preview', defaultMessage: 'Preview' },
    description: {
        id: 'languageStudio.generateQuestionsDemoPage.description',
        defaultMessage: `This capability uses state-of-art turing language
         generation models to AI generate questions on your content.
          The content can be in the form of documents, URLs or raw text.`
    },
    languageInfo: {
        id: 'languageStudio.generateQuestionsDemoPage.languageInfo',
        defaultMessage: `Question generation is currently only
    available in English. More languages will be added over time.`
    },
    emptyState: {
        id: 'languageStudio.generateQuestionsDemoPage.emptyState',
        defaultMessage: `Your generated QnA results will appear here once you enter or upload some text and press Run.`
    },
    signedOutEmptyState: {
        id: 'languageStudio.generateQuestionsDemoPage.signedOutEmptyState',
        defaultMessage: `Your Generate QnA results will appear here once you press Run.`
    },
    textInput: {
        id: 'languageStudio.generateQuestionsDemoPage.textInput',
        defaultMessage: 'Text'
    },
    urlInput: {
        id: 'languageStudio.generateQuestionsDemoPage.urlInput',
        defaultMessage: 'URL'
    },
    urlPlaceholder: {
        id: 'languageStudio.generateQuestionsDemoPage.urlPlaceholder',
        defaultMessage: 'Enter a publicly accessible URL'
    },
    urlLabel: {
        id: 'languageStudio.generateQuestionsDemoPage.urlLabel',
        defaultMessage: 'Enter your URL'
    },
    urlLabelNotSignedIn: {
        id: 'languageStudio.generateQuestionsDemoPage.urlLabelNotSignedIn',
        defaultMessage: 'Use one of our sample URLs below to try out the experience'
    }
});

export const GenerateQuestionsDemoPage = observer(() => {
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isSignedIn } = rootStore.authStore;
    const { qnaAnswer, tracker, resetResults, generateQAs, getGenerateQnACurlCommand } = useQuestionsAnswersGenerator();

    const [document, setDocument] = React.useState('');
    const [selectedSampleTitle, setSelectedSampleTitle] = React.useState<string>(null);

    const [selectedPivotKey, setSelectedPivotKey] = React.useState<string>('text');

    const mappedQnAs = React.useMemo(() => {
        const qnAs = new Map<string, MappedQuestions>();

        qnaAnswer?.generatedQuestions.map(item => {
            if (!qnAs.has(item.answer)) {
                qnAs.set(item.answer, {
                    questions: [],
                    confidenceScore: []
                });
            }

            qnAs.get(item.answer).questions.push(item.question);
            qnAs.get(item.answer).confidenceScore.push(item.confidenceScore);
        });
        return qnAs;
    }, [qnaAnswer]);

    const handleDocumentChange = (newDocument: string) => {
        if (qnaAnswer || tracker.state === 'running') {
            resetResults();
        }

        setDocument(newDocument);
    };

    const handleSampleClick = (sample: DemoSample) => {
        setSelectedSampleTitle(sample?.title);
    };

    const handlePivotChange = (pivotKey: string) => {
        setSelectedPivotKey(pivotKey);
        resetResults();
        setDocument('');
        logInputPivotClick('Generate questions', pivotKey);

        if (!isSignedIn) {
            const demoSample = generateQuestionsDemoSamples(pivotKey)[0];
            handleSampleClick(demoSample);
            handleDocumentChange(demoSample?.document ?? '');
        }
    };

    const getDemoInput = () => (
        <DocumentInputStep
            document={document}
            disableLanguageSelection
            selectedLanguage={'en-us'}
            documentLimit={LuisConstants.QuestionGeneration.maxDocumentLimit}
            featureName="Generate questions"
            samplesList={generateQuestionsDemoSamples(selectedPivotKey)}
            onDocumentChange={handleDocumentChange}
            onSampleClick={handleSampleClick}
            displayTitle={false}
            hideFilesUploader={selectedPivotKey === 'url'}
            textFieldProps={
                selectedPivotKey === 'url' && {
                    label: localize(isSignedIn ? messages.urlLabel : messages.urlLabelNotSignedIn),
                    placeholder: localize(messages.urlPlaceholder),
                    multiline: true,
                    styles: { fieldGroup: { minHeight: 'auto' } }
                }
            }
            acceptedResourceType="TextAnalytics">
            <DemoPageAgreement
                featureName="Generate questions"
                isRunDisabled={!document.length}
                acceptedResourceType="TextAnalytics"
                pricingLink={textAnalyticsPricingLink}
                onRunDemoClick={() => {
                    generateQAs({
                        document,
                        language: 'en',
                        docKind: selectedPivotKey === 'text' ? 'raw' : 'url',
                        sampleTitle: selectedSampleTitle
                    });
                }}
            />
        </DocumentInputStep>
    );

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <DemoHeader
                featureName="Generate questions"
                supportedPlatforms={['docker', 'cloud']}
                demoVersion={LuisConstants.QuestionGeneration.ApiVersion}
                documentationLink={generateQuestionsDocLink}
                gitHubSamplesLink={generateQuestionsSamplesLink}
                learnMoreLink={generateQuestionsDocLink}
                demoTitle={localize(messages.pageHeader)}
                secondaryText={localize(messages.preview)}
                demoDescription={localize(messages.description)}
            />

            <LuisText>{localize(messages.languageInfo)}</LuisText>

            <Pivot linkSize="large" selectedKey={selectedPivotKey} onLinkClick={item => handlePivotChange(item.props.itemKey)}>
                <PivotItem itemKey={'text'} headerText={localize(messages.textInput)} />
                <PivotItem itemKey={'url'} headerText={localize(messages.urlInput)} />
            </Pivot>

            {getDemoInput()}

            <ResultsStep<GeneratedQAsResult>
                results={qnaAnswer}
                isLoading={tracker.isRunning}
                featureName="Generate questions"
                emptyStateMessage={localize(isSignedIn ? messages.emptyState : messages.signedOutEmptyState)}>
                <VisualizedGenerateQuestionsResult generatedQnAs={mappedQnAs} />
            </ResultsStep>

            <NextStepsStep
                isResultsEmpty={!qnaAnswer}
                featureName="Generate questions"
                sampleCodeLink={generateQuestionsSamplesLink}
                demosToDisplay={['questionsAnswering', 'customQuestionsAnswering']}
                curlCommand={getGenerateQnACurlCommand({
                    language: 'en',
                    content: document,
                    docKind: selectedPivotKey === 'text' ? 'raw' : 'url'
                })}
                documentationLink={generateQuestionsDocLink}
            />
        </Stack>
    );
});
